import * as Yup from "yup";
import { requiredTextFieldMinMax } from "./commonValidationSchemas";
import { CUSTOM_EMAIL } from "../emailUtils";
import validationRules from "utils/validationRules";
import { labelValues } from "./tagsValidationSchema";
import { PriorityLevels } from "enums";

const subjectLengths = {
  min: 3,
  max: 512,
};

const previewTextMaxLength = 2048;

const feedbackMessageMaxLength = 512;

const validateFileType = (files: any) => {
  const file = files[0];
  // We do not have fileType returned from backend and assume that an empty value is a valid value
  return !file?.type || validationRules.mp4m4vFileTypes(files);
};

const subject = (): Yup.StringSchema<string | undefined, object> =>
  Yup.string()
    .trim()
    .required("Subject Line is required")
    .test(
      "subject_restricted_domains",
      "Subject Line contains restricted domain(s)",
      validationRules.hasNoBrainstormUrls,
    )
    .min(subjectLengths.min, `Subject Line must have at least ${subjectLengths.min} characters`)
    .max(subjectLengths.max, `Subject Line cannot exceed ${subjectLengths.max} characters`);

const previewText = (): Yup.StringSchema<string | undefined, object> =>
  Yup.string().trim().max(previewTextMaxLength, `Preview Text cannot exceed ${previewTextMaxLength} characters`);

const uploadedVideos = () =>
  Yup.mixed()
    .test("File does not exist", "Feedback Video is required", validationRules.fileListLength)
    .test("File size is equal to zero", "File size is equal to zero", validationRules.fileMinSize)
    .test("File exceeds maximum size", "File cannot exceed 4GB", validationRules.fileMaxSize)
    .test("Wrong file type", "Only mp4, m4v file types are allowed", validateFileType)
    .test(
      "File name size exceeds maximum size",
      "File name cannot exceed 120 characters",
      validationRules.fileNameSize,
    );

const feedbackMessage = (): Yup.StringSchema<string | undefined, object> =>
  Yup.string()
    .trim()
    .max(feedbackMessageMaxLength, `Feedback Message cannot exceed ${feedbackMessageMaxLength} characters`);

export const getCampaignConfigureSchema = () => {
  return () => {
    const title = () => {
      return requiredTextFieldMinMax("Title", 3, 64);
    };

    const remedialTraining = Yup.object().shape({
      flowTitle: Yup.string().when("triggerTypeId", {
        is: (triggerTypeId?: number) => !!triggerTypeId,
        then: (schema) => schema.trim().required(),
        otherwise: (schema) => schema.notRequired(),
      }),
      daysToComplete: Yup.number().when(["priority", "isFixedDueDate", "flowId", "triggerTypeId"], {
        is: (priority: PriorityLevels, isFixedDueDate: boolean, flowId?: number, triggerTypeId?: number) =>
          !isFixedDueDate && !!flowId && !!triggerTypeId && priority == PriorityLevels.required,
        then: (schema) => schema.required("Days to complete is required").max(100, "Maximum value is 100"),
        otherwise: (schema) => schema.notRequired(),
      }),
      fixedDueDate: Yup.string().when(["priority", "isFixedDueDate", "flowId", "triggerTypeId"], {
        is: (priority: PriorityLevels, isFixedDueDate: boolean, flowId?: number, triggerTypeId?: number) =>
          isFixedDueDate && !!flowId && !!triggerTypeId && priority == PriorityLevels.required,
        then: (schema) => schema.trim().required("Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    });

    return Yup.object().shape({
      name: title(),
      campaignTypeId: Yup.number().positive("Select Campaign Type").required(),
      subDomainId: Yup.number().positive("Select Subdomain").required(),
      labels: labelValues,
      remedialTraining,
    });
  };
};

export const emailTemplate = Yup.object().shape({
  senderName: requiredTextFieldMinMax("Sender Name", 1, 64),
  senderEmail: requiredTextFieldMinMax("Sender Email", 3, 256).matches(CUSTOM_EMAIL, "Invalid Sender Email"),
  subject: subject(),
  previewText: previewText(),
  html: validationRules.htmlWithoutBrainstormDomains().required("Body is required"),
});

export const landingPage = Yup.object().shape({
  html: validationRules.htmlWithoutBrainstormDomains().required("Body is required"),
});

export const feedbackPage = Yup.object().shape({
  uploadedVideos: uploadedVideos(),
  message: feedbackMessage(),
});
